import { supabase } from "../../../lib/supabase";
import { upsertContact, syncConversation } from "./syncDbHelpers";
import { isUsableContactName, extractChatImageUrl } from "./historyFetchers";
import { isTemporaryRemoteMediaUrl } from "../../../utils/failedImageCache";
import { createSyntheticFallback } from "./syntheticMessage";
import { mapAndFilterMessages } from "./historyMessageMapper";
import { persistMappedMessages } from "./historyMessageRepository";

export interface ProcessChatContext {
  chat: any;
  instanceName: string;
  connectionId: string;
  organizationId: string;
  integrationUserId?: string;
  ownerJid: string;
  remoteJid: string;
  chatMessages: any[];
  messagesPerChat: number;
}

export interface ProcessChatResult {
  hasError: boolean;
  insertedCount: number;
  syntheticCreated: boolean;
}

/**
 * processChatHistory - Sincroniza um único chat
 * Atua como **Use Case** orquestrador:
 * 1. Prepara contatos e a conversa no banco.
 * 2. Mapeia e sanitiza (parse) os payloads brutos na memória.
 * 3. Delega a persistência (DB) das mensagens purificadas ao Repository.
 */
export async function processChatHistory(
  context: ProcessChatContext,
): Promise<ProcessChatResult> {
  const {
    chat,
    instanceName,
    connectionId,
    organizationId,
    integrationUserId,
    ownerJid,
    remoteJid,
    chatMessages,
    messagesPerChat,
  } = context;

  const rawChat = chat as any;
  const contactPhone = (chat.phone || remoteJid)
    .replace("@s.whatsapp.net", "")
    .replace(/\D/g, "");
  let contactName =
    rawChat.wa_contactName ||
    rawChat.wa_name ||
    rawChat.name ||
    chat.name ||
    contactPhone;

  if (!isUsableContactName(contactName, contactPhone)) {
    try {
      const { fetchChatDetails, resolveContactNameFromChat } = await import("./historyFetchers");
      const details = await fetchChatDetails(instanceName, contactPhone);
      contactName = resolveContactNameFromChat(details, contactPhone, contactPhone);
    } catch {
      /* mantém fallback com telefone */
    }
  }

  // 1. Snapshot dos Metadados — prioriza chat/details (pefil.md)
  let profilePicUrl = extractChatImageUrl(rawChat);

  if (!profilePicUrl) {
    try {
      const { fetchChatDetails } = await import("./historyFetchers");
      const details = await fetchChatDetails(instanceName, contactPhone);
      profilePicUrl = extractChatImageUrl(details);
    } catch {
      /* opcional */
    }
  }

  // 2. Garante a existência do Contato
  const contactId = await upsertContact(
    contactPhone,
    contactName,
    organizationId,
    profilePicUrl,
  );

  // 3. Garante que a Conversa existe e está vinculada (Upsert)
  if (profilePicUrl && !rawChat.imagePreview) {
    rawChat.imagePreview = profilePicUrl;
  }

  const { conversationDbId, hasError: convError } = await syncConversation(
    connectionId,
    remoteJid,
    contactId,
    contactName,
    contactPhone,
    organizationId,
    integrationUserId,
    rawChat,
    chat,
  );

  if (convError || !conversationDbId) {
    return { hasError: true, insertedCount: 0, syntheticCreated: false };
  }

  // 4. Persiste avatar permanente via edge function (R2) quando necessário
  if (contactId) {
    try {
      await supabase.functions.invoke('sync-contact-avatar', {
        body: {
          contactId,
          phone: contactPhone,
          organizationId,
          instanceName,
          connectionId,
          force: !profilePicUrl || isTemporaryRemoteMediaUrl(String(profilePicUrl)),
        },
      });
    } catch {
      /* opcional */
    }
  }

  // 5. Verifica se o Global Fetcher de Histórico trouxe dados para ESSE chat
  if (!chatMessages.length) {
    await createSyntheticFallback(
      rawChat,
      conversationDbId,
      remoteJid,
      organizationId,
      ownerJid,
    );
    return { hasError: false, insertedCount: 0, syntheticCreated: true };
  }

  // 6. MAPEAR: Filtra, resolve mídia, formata os tipos e retira duplicatas (SRP Helper)
  const mapped = await mapAndFilterMessages(
    chatMessages,
    messagesPerChat,
    ownerJid,
    instanceName,
  );

  if (!mapped.length) {
    return { hasError: false, insertedCount: 0, syntheticCreated: false };
  }

  // 7. PERSISTIR: Compara com banco existente, remove defasados e salva o lote novo
  const insertedCount = await persistMappedMessages(
    mapped,
    conversationDbId,
    organizationId,
    ownerJid,
    remoteJid,
  );

  return { hasError: false, insertedCount, syntheticCreated: false };
}
